import { cn } from '@/lib/utils';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'shimmer' | 'pulse';
}

const SKELETON_CHART_HEIGHTS = [38, 54, 62, 49, 71, 64, 45, 58, 41, 67, 60, 52];

function Skeleton({ className, variant = 'shimmer', ...props }: SkeletonProps) {
  return (
    <div
      className={cn(
        'rounded-md bg-muted',
        variant === 'shimmer' && 'skeleton-shimmer',
        variant === 'pulse' && 'animate-pulse',
        variant === 'default' && 'bg-slate-700/30',
        className,
      )}
      {...props}
    />
  );
}

// Predefined skeleton patterns for common UI elements
function SkeletonCard({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('quantforge-card p-6', className)} {...props}>
      <Skeleton className="h-4 w-1/3 mb-4" />
      <Skeleton className="h-8 w-2/3 mb-2" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  );
}

interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  density?: 'compact' | 'normal' | 'spacious';
}

function SkeletonTable({
  rows = 5,
  columns = 4,
  showHeader = true,
  density = 'normal',
}: SkeletonTableProps) {
  const rowHeight = density === 'compact' ? 'h-8' : density === 'spacious' ? 'h-14' : 'h-10';
  const headerHeight = density === 'compact' ? 'h-6' : 'h-8';

  return (
    <div className="quantforge-card overflow-hidden">
      {showHeader && (
        <div className="flex gap-4 border-b border-white/5 bg-white/[0.02] p-4">
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} className={cn(headerHeight, 'flex-1')} />
          ))}
        </div>
      )}
      <div className="divide-y divide-white/5">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="flex gap-4 p-4 animate-fade-in"
            style={{ animationDelay: `${i * 50}ms` }}
          >
            {Array.from({ length: columns }).map((_, j) => (
              <Skeleton key={j} className={cn(rowHeight, 'flex-1')} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SkeletonTableRow({ columns = 4 }: { columns?: number }) {
  return (
    <tr className="animate-fade-in border-b border-white/5">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="p-4">
          <Skeleton className="h-6 w-full" />
        </td>
      ))}
    </tr>
  );
}

function SkeletonChart({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('quantforge-card p-6', className)} {...props}>
      <Skeleton className="h-4 w-1/4 mb-6" />
      <div className="flex items-end gap-1 h-48">
        {SKELETON_CHART_HEIGHTS.map((height, i) => (
          <Skeleton
            key={i}
            className="flex-1 animate-fade-in"
            style={{
              height: `${height}%`,
              animationDelay: `${i * 50}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function SkeletonKpiCard({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('quantforge-card p-6', className)} {...props}>
      <div className="flex items-center space-x-3">
        <Skeleton className="w-12 h-12 rounded-xl" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    </div>
  );
}

function SkeletonList({ items = 5 }: { items?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: items }).map((_, i) => (
        <div
          key={i}
          className="animate-fade-in rounded-lg border border-white/5 bg-white/[0.03] p-3"
          style={{ animationDelay: `${i * 50}ms` }}
        >
          <Skeleton className="w-10 h-10 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="w-16 h-8 rounded-md" />
        </div>
      ))}
    </div>
  );
}

export {
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  SkeletonTableRow,
  SkeletonChart,
  SkeletonKpiCard,
  SkeletonList,
};
